/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-07-11
 */

import React, {useRef, useState } from "react";
import { postFormMultiPartFormData } from "../../utils/AppUtil";
import { SpinnerLoading } from "../../utils/SpinnerLoading";
import AvatarEditor from 'react-avatar-editor';
import { readFile } from "node:fs/promises";

type FormDataType = {
    deceasedId: string,
    firstName: string, 
    lastName: string, 
    subscriberEmail: string, 
    picture: string,
    pictureType: string,
    yahrzeitReminder: boolean
}

type ModalProps = {
    endpoint: string;
    deceasedId: string;
    firstName: string;
    lastName: string;
}

export const SubmitPictureModal: React.FC<ModalProps> = (props) => {
    
    const initForm: FormDataType = {
        deceasedId: props.deceasedId,
        firstName: "", 
        lastName: "", 
        subscriberEmail: "", 
        picture: "", 
        pictureType: "PROFILE", 
        yahrzeitReminder: true
    }
    
    const [formState, setFormState] = useState<FormDataType>(initForm);

    const [modalBody, setModalBody]: any = useState(true);
    const [modalLoading, setModalLoading]: any = useState(false);
    const [modalMessageSuccess, setModalMessageSuccess]: any = useState(false);
    const [modalMessageError, setModalMessageError]: any = useState(false);

    const [image, setImage] = useState<File>();
    const [scale, setScale] = useState(1);
    const [previewLink, setPreviewLink] = useState("");
    const [previewShow, setPreviewShow] = useState(false);

    const editorRef = useRef<AvatarEditor>(null!);
    const pictureInputRef = useRef<HTMLInputElement>(null!);

    const handlePreview = () => {
        const canvasImage: HTMLCanvasElement = editorRef.current.getImage();

        canvasImage.toBlob((blob: any) => {

            if(pictureInputRef.current.files?.length) {
                const name = pictureInputRef.current.files[0].name;
                const file = new File( [ blob ], name );
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                pictureInputRef.current.files = dataTransfer.files;
                setFormState((formState): FormDataType => {
                    const form = {...formState};
                    form.picture = blob;
                    return form;
                });               
            }
            const previewURL = URL.createObjectURL(blob);
            setPreviewLink(previewURL);
        });

        setPreviewShow(current => !current);
    }

    const handlePreviewToggle = () => {
        setPreviewShow(current => !current);
        setScale(1);
    }

    const handleFormChange = (e: React.ChangeEvent<any>) => {

        if(e.target.type === "checkbox") {
            setFormState((formState) => ({
                ...formState, 
                [e.target.id]: e.target.checked
            }));
        }

        if(e.target.type === "text" || e.target.type === "email") {
            setFormState((formState) => ({
                ...formState, 
                [e.target.id]: e.target.value
            }));
        }

        if(e.target.type === "file" && e.target.files != null) {
            setImage(e.target.files[0]);
            setFormState((formState) => ({
                ...formState, 
                [e.target.id]: e.target.files[0]
            }));
        }

    };

    const handleMessageSuccessShow = (e?: any) => {
        setModalBody(false);
        setModalMessageSuccess(true);
    };

    const handleMessageErrorShow = (e?: any) => {
        setModalBody(false);
        setModalMessageError(true);
    };

    const handleMessageHide = (e?: any) => {
        setTimeout( ()=> {
            setModalBody(true);
            setModalMessageSuccess(false);
            setModalMessageError(false);
        }, 500)
    };

    const handlePost = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setModalLoading(true);

        const formData = new FormData();
        formData.set("deceasedId", formState.deceasedId);
        formData.set("firstName", formState.firstName);
        formData.set("lastName", formState.lastName);
        formData.set("file", formState.picture);
        formData.set("pictureType", formState.pictureType);
        formData.set("subscriberEmail", formState.subscriberEmail);
        if(formState.yahrzeitReminder) {
            formData.append("deceasedId", "true");
        }
        
        postFormMultiPartFormData(
            props.endpoint,
            formData,
            (response: any) => {
                setModalLoading(false);
                handleMessageSuccessShow();
            },
            (response: any) => { // error
                setModalLoading(false);
                handleMessageErrorShow();
            }
        );
    }

    return (
        <form  onSubmit={handlePost}>
        <input id="deceasedId" name="deceasedId" type="hidden" value={formState.deceasedId}/>


            <div className="modal fade" id="modalSubmitPicture" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex={-1} aria-labelledby="modalSubmitPictureLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-scrollable">
                    <div className="modal-content">
                    
                        <div className="modal-header">
                            <h1 className="modal-title fs-4 jc-txt-heading " id="modalSubmitPictureLabel">Submit Picture</h1>
                            <button onClick={handleMessageHide} id="closeModalMessage" type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>

                        {modalLoading && 
                            <div className="d-flex justify-content-center">
                                <SpinnerLoading />
                            </div>
                        }

                        {modalMessageSuccess && 
                            <>
                                <div className="modal-body d-flex justify-content-center">
                                    <div className="d-flex flex-column gap-3" style={{alignItems: "center"}}>
                                        <svg width="201" height="200" viewBox="0 0 201 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="0.5" width="200" height="200" rx="100" fill="#198754"/>
                                            <path d="M91.1251 112.919L72.1698 93.9572L72.1696 93.9571C71.3953 93.1827 70.3451 92.7477 69.25 92.7477C68.155 92.7477 67.1048 93.1827 66.3304 93.9571L67.0307 94.6573L66.3304 93.9571C65.5561 94.7314 65.1211 95.7816 65.1211 96.8767C65.1211 97.9717 65.5561 99.022 66.3304 99.7963L67.0281 99.0986L66.3304 99.7963L88.2045 121.67L91.1251 112.919ZM91.1251 112.919L131.955 72.083C131.955 72.0827 131.955 72.0824 131.955 72.0821C132.338 71.6984 132.793 71.3939 133.294 71.1861L133.677 72.1084L133.294 71.1861C133.795 70.9781 134.332 70.8711 134.875 70.8711C135.418 70.8711 135.955 70.9781 136.456 71.1861C136.957 71.3939 137.412 71.6983 137.795 72.0821M91.1251 112.919L137.795 77.9213C138.178 77.5383 138.483 77.0834 138.691 76.5827C138.899 76.0815 139.006 75.5443 139.006 75.0017C139.006 74.4591 138.899 73.9219 138.691 73.4207C138.483 72.92 138.178 72.4651 137.795 72.0821M137.795 72.0821C137.795 72.0824 137.795 72.0827 137.796 72.083L137.088 72.7892L137.794 72.0812C137.794 72.0815 137.794 72.0818 137.795 72.0821ZM94.0455 121.67L137.794 77.9222L94.0455 121.67ZM92.706 122.567C93.2068 122.359 93.6617 122.055 94.0446 121.671H88.2054C88.5884 122.055 89.0433 122.359 89.5441 122.567C90.0452 122.775 90.5825 122.882 91.125 122.882C91.6676 122.882 92.2049 122.775 92.706 122.567Z" fill="white" stroke="white" strokeWidth="2"/>
                                        </svg>
                                        <p className="jc-txt-body jc-txt-dark fw-6" style={{textAlign: "center"}}>
                                            Thanks for submitting photo. Your photo upload was sent for approval, you will be noticed via email when upload is complete
                                        </p>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button onClick={handleMessageHide} id="closeModalMessage" type="button" className="btn btn-dark jc-txt-body jc-txt-light" data-bs-dismiss="modal" aria-label="Close">OK</button>
                                </div>
                            </>
                        }

                        {modalMessageError && 
                            <>
                                <div className="modal-body d-flex justify-content-center">
                                    <div className="d-flex flex-column gap-3">
                                        <svg width="201" height="200" viewBox="0 0 201 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="0.5" width="200" height="200" rx="100" fill="#DC3545"/>
                                            <path d="M94.6667 100.002L78.8305 115.832L78.8304 115.832C78.447 116.215 78.1428 116.671 77.9353 117.172L78.8592 117.554L77.9353 117.172C77.7278 117.673 77.621 118.209 77.621 118.752C77.621 119.294 77.7278 119.831 77.9353 120.332L78.8592 119.949L77.9353 120.332C78.1428 120.833 78.447 121.288 78.8304 121.671C79.2138 122.055 79.6689 122.359 80.1699 122.566C80.6708 122.774 81.2078 122.881 81.75 122.881C82.2922 122.881 82.8291 122.774 83.33 122.566C83.831 122.359 84.2862 122.055 84.6696 121.671L84.6697 121.671L100.5 105.835L116.33 121.671L116.33 121.671C116.714 122.055 117.169 122.359 117.67 122.566C118.171 122.774 118.708 122.881 119.25 122.881C119.792 122.881 120.329 122.774 120.83 122.566C121.331 122.359 121.786 122.055 122.17 121.671C122.553 121.288 122.857 120.833 123.065 120.332C123.272 119.831 123.379 119.294 123.379 118.752C123.379 118.209 123.272 117.673 123.065 117.172C122.857 116.671 122.553 116.215 122.17 115.832L122.169 115.832L106.333 100.002L122.169 84.1714L122.17 84.1713C122.553 83.7879 122.857 83.3327 123.065 82.8318C123.272 82.3308 123.379 81.7939 123.379 81.2517C123.379 80.7095 123.272 80.1725 123.065 79.6716C122.857 79.1707 122.553 78.7155 122.17 78.3321C121.786 77.9487 121.331 77.6445 120.83 77.437L120.447 78.3609L120.83 77.437C120.329 77.2295 119.792 77.1227 119.25 77.1227C118.708 77.1227 118.171 77.2295 117.67 77.437L118.053 78.3609L117.67 77.437C117.169 77.6445 116.714 77.9487 116.33 78.3321L116.33 78.3322L100.5 94.1684L84.6705 78.333C84.6703 78.3328 84.6702 78.3327 84.6701 78.3326C84.287 77.9486 83.8319 77.644 83.3309 77.4361L82.9477 78.3597L83.3309 77.4361C82.8298 77.2281 82.2925 77.1211 81.75 77.1211C81.2074 77.1211 80.6701 77.2281 80.169 77.4361C79.6678 77.644 79.2126 77.9488 78.8295 78.333L79.5375 79.0392M94.6667 100.002L79.5375 79.0392M94.6667 100.002L78.8313 84.1722C78.8311 84.172 78.831 84.1719 78.8309 84.1718M94.6667 100.002L78.8309 84.1718M79.5375 79.0392C79.2464 79.3295 79.0156 79.6743 78.858 80.054C78.7005 80.4336 78.6194 80.8406 78.6194 81.2517C78.6194 81.6627 78.7005 82.0697 78.858 82.4494M79.5375 79.0392L78.8313 78.3312C78.4471 78.7144 78.1423 79.1696 77.9344 79.6707C77.7264 80.1719 77.6194 80.7091 77.6194 81.2517C77.6194 81.7943 77.7264 82.3315 77.9344 82.8327L78.858 82.4494M78.858 82.4494L77.9344 82.8327C78.1423 83.3336 78.4469 83.7887 78.8309 84.1718M78.858 82.4494L78.8309 84.1718" fill="white" stroke="white" strokeWidth="2"/>
                                        </svg>
                                        <p className="jc-txt-body jc-txt-dark fw-6" style={{textAlign: "center"}}>Error Message</p>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button onClick={handleMessageHide} id="closeModalMessage" type="button" className="btn btn-dark jc-txt-body jc-txt-light" data-bs-dismiss="modal" aria-label="Close">Try Again Later</button>
                                </div>
                            </>
                        }

                        {modalBody && 
                            <>
                                <div className="modal-body">
                                    <div className="d-flex row">

                                            {image ?
                                                <div className="col-12 col-md-6 p-2">
                                                    {previewShow ?
                                                        <div className="d-flex flex-column align-items-center">
                                                            <img src={previewLink} style={{width: "250px", height: "250px", borderRadius: "50%"}}/>
                                                            <div className="d-flex justify-content-end mt-3 mx-auto" style={{width: "250px"}}>
                                                                <button type="button" onClick={handlePreviewToggle} className="deceased-person-button btn btn-outline-dark jc-txt-body" style={{width: "142px"}}>Reset</button>
                                                            </div>
                                                        </div>
                                                    :
                                                        <div className="d-flex flex-column align-items-center">
                                                            <AvatarEditor ref={editorRef} image={image} width={250} height={250} border={0} borderRadius={500} scale={scale} rotate={0} style={{borderRadius: "8px"}} />
                                                            <div className="d-flex justify-content-between gap-3 mt-3 mx-auto" style={{width: "250px"}}>
                                                                <div className="d-flex gap-1">
                                                                    <button type="button" onClick={() => setScale(scale + .1)} className="btn-image-scale-plus" />
                                                                    <button type="button" onClick={() => setScale(scale - .1)} className="btn-image-scale-minus" />
                                                                </div>
                                                                <button type="button" onClick={handlePreview} className="deceased-person-button btn btn-outline-dark jc-txt-body" style={{width: "142px"}}>Save & Preview</button>
                                                            </div>
                                                        </div>
                                                    }
                                                </div>
                                            :
                                                <div className="col-12 col-md-6 p-2 py-4">
                                                    <div className="d-flex justify-content-center pb-3">
                                                        <img src="/images/camera.png" />
                                                    </div>
                                                    <p className="jc-txt-body jc-txt-dark fw-6">
                                                        Submit a Picture of <span className="fw-bold">{props.firstName} {props.lastName}.</span><br/><br/>
                                                        The picture submitted will be reviewed for publication. 
                                                        You will receive an email to confirm when the picture will be online.
                                                    </p>
                                                </div>
                                            }
            
                                        <div className="col-12 col-md-6 p-2">
                                            <div className="d-flex flex-column gap-1 pb-2">
                                                <label htmlFor="name" className="jc-txt-body jc-txt-dark fw-6">Name</label>
                                                <input id="name" name="name" type="text" className="form-control" placeholder="Your First Name" onChange={handleFormChange} />
                                            </div>
                                            <div className="d-flex flex-column gap-1 pb-2">
                                                <label htmlFor="surname" className="jc-txt-body jc-txt-dark fw-6">Surname</label>
                                                <input id="surname" name="surname" type="text" className="form-control" placeholder="Your Surname" onChange={handleFormChange} />
                                            </div>
                                            <div className="d-flex flex-column gap-1 pb-2">
                                                <label htmlFor="subscriberEmail" className="jc-txt-body jc-txt-dark fw-6">Email</label>
                                                <input id="subscriberEmail" name="subscriberEmail" type="email" className="form-control" placeholder="Please use a valid email" value={formState.subscriberEmail} onChange={handleFormChange} />
                                            </div>

                                            <div className="d-flex flex-column gap-1 pb-2">
                                                <label htmlFor="picture" className="jc-txt-body jc-txt-dark fw-6">{props.firstName} {props.lastName}'s Picture</label>
                                                <input ref={pictureInputRef} id="picture" name="picture" type="file" className="form-control" onChange={handleFormChange} />
                                            </div>

                                            <div className="form-check d-flex gap-2 p-2 ps-4">
                                                <input id="yahrzeitReminder" name="yahrzeitReminder" type="checkbox" className="form-check-input" checked={formState.yahrzeitReminder} onChange={handleFormChange} />
                                                <label className="form-check-label jc-txt-body jc-txt-dark fw-6" htmlFor="yahrzeitReminder">Remind me about {props.firstName} {props.lastName}'s Yahrzeit</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="submit" className="btn-modal btn btn-dark jc-txt-body jc-txt-light">Submit</button>
                                </div>
                            </>
                        }
                    </div>
                </div>
            </div>
        </form>
    );
}